import {
  deleteMartialPerks,
  putMartialPerks,
} from "@/common/character/martialPerks";
import { putMartialPerkGain } from "@/common/character/martialPerkGain";

export interface MartialPerksStore {
  rank1: string;
  rank2: string;
  rank3: string;
  rank4: string;
  rank5: string;
  gainIds: Array<number>;
  martialPerks: {
    defensive: Array<MartialPerkItem>;
    offensive: Array<MartialPerkItem>;
    supportive: Array<MartialPerkItem>;
    utility: Array<MartialPerkItem>;
  };
}

export interface MartialPerkItem {
  name: string;
  rank: number;
  known: boolean;
  id: number;
}

export default {
  namespaced: true,
  state: (): MartialPerksStore => ({
    rank1: "none",
    rank2: "none",
    rank3: "none",
    rank4: "none",
    rank5: "none",
    gainIds: [0, 0, 0, 0, 0],
    martialPerks: {
      defensive: [
        { name: "Defensive Maneuvers", rank: 1, known: false, id: 0 },
        { name: "Dodging", rank: 1, known: false, id: 0 },
        { name: "Mitigative", rank: 1, known: false, id: 0 },
        { name: "Steady Hand", rank: 2, known: false, id: 0 },
        { name: "Countermeasures", rank: 2, known: false, id: 0 },
        { name: "Lifelink Aura", rank: 3, known: false, id: 0 },
        { name: "Relentless Defender", rank: 3, known: false, id: 0 },
        { name: "Shieldbearer", rank: 4, known: false, id: 0 },
        { name: "Immaculate Deftness", rank: 4, known: false, id: 0 },
        { name: "Auric Defense", rank: 5, known: false, id: 0 },
        { name: "Perfect Defense", rank: 5, known: false, id: 0 },
      ],
      offensive: [
        { name: "Berserker's Frenzy", rank: 1, known: false, id: 0 },
        { name: "Substance", rank: 1, known: false, id: 0 },
        { name: "Sixth Sense", rank: 2, known: false, id: 0 },
        { name: "Bladedancer", rank: 2, known: false, id: 0 },
        { name: "Mighty Rage", rank: 2, known: false, id: 0 },
        { name: "Risk Taker", rank: 3, known: false, id: 0 },
        { name: "Explosive Attacks", rank: 3, known: false, id: 0 },
        { name: "Bloodlust", rank: 4, known: false, id: 0 },
        { name: "Overdrive", rank: 4, known: false, id: 0 },
        { name: "Unstoppable Bloodrush", rank: 5, known: false, id: 0 },
        { name: "Indomitable", rank: 5, known: false, id: 0 },
      ],
      supportive: [
        { name: "Knight Protector", rank: 1, known: false, id: 0 },
        { name: "Mark and Muse", rank: 1, known: false, id: 0 },
        { name: "Mechanist", rank: 2, known: false, id: 0 },
        { name: "Sacrifice", rank: 2, known: false, id: 0 },
        { name: "Tactical Aid", rank: 3, known: false, id: 0 },
        { name: "Orchestrated Warfare", rank: 3, known: false, id: 0 },
        { name: "Enguard", rank: 3, known: false, id: 0 },
        { name: "Spirit Guardian", rank: 4, known: false, id: 0 },
        { name: "Team Player", rank: 4, known: false, id: 0 },
        { name: "Judgement", rank: 5, known: false, id: 0 },
        { name: "Chronotrigger", rank: 5, known: false, id: 0 },
      ],
      utility: [
        { name: "Disruptor", rank: 1, known: false, id: 0 },
        { name: "Wrestling", rank: 1, known: false, id: 0 },
        { name: "Viscous Coating", rank: 1, known: false, id: 0 },
        { name: "Traps", rank: 1, known: false, id: 0 },
        { name: "Shadow Trickery", rank: 2, known: false, id: 0 },
        {
          name: "Hexshot Rigging",
          rank: 3,
          known: false,
          id: 0,
        },
        { name: "Absolute Focus", rank: 3, known: false, id: 0 },
        { name: "Phantom", rank: 3, known: false, id: 0 },
        { name: "Theatrics", rank: 4, known: false, id: 0 },
        { name: "Trapper", rank: 4, known: false, id: 0 },
        { name: "Mystic Warrior", rank: 5, known: false, id: 0 },
        { name: "Hexsmith", rank: 5, known: false, id: 0 },
      ],
    },
  }),
  getters: {
    gainIds: (state: MartialPerksStore) => {
      return state.gainIds;
    },
    martialPerks: (state: MartialPerksStore) => {
      return state.martialPerks;
    },
    martialGain: (state: MartialPerksStore) => {
      return [state.rank1, state.rank2, state.rank3, state.rank4, state.rank5];
    },
  },
  mutations: {
    setMartialPerks: (
      state: MartialPerksStore,
      newData: {
        defensive: Array<MartialPerkItem>;
        offensive: Array<MartialPerkItem>;
        supportive: Array<MartialPerkItem>;
        utility: Array<MartialPerkItem>;
      }
    ) => {
      state.martialPerks = newData;
    },
    setMartialGain: (
      state: MartialPerksStore,
      newData: { ranks: string[] }
    ) => {
      state.rank1 = newData.ranks[0];
      state.rank2 = newData.ranks[1];
      state.rank3 = newData.ranks[2];
      state.rank4 = newData.ranks[3];
      state.rank5 = newData.ranks[4];
    },
    setMartialGainId: (
      state: MartialPerksStore,
      newData: { position: number; id: number }
    ) => {
      state.gainIds[newData.position] = newData.id;
    },
    setMartialGainFromDb: (
      state: MartialPerksStore,
      newData: { rank: number; name: string; id: number }
    ) => {
      switch (newData.rank) {
        case 1:
          state.rank1 = newData.name;
          state.gainIds[0] = newData.id;
          break;
        case 2:
          state.rank2 = newData.name;
          state.gainIds[1] = newData.id;
          break;
        case 3:
          state.rank3 = newData.name;
          state.gainIds[2] = newData.id;
          break;
        case 4:
          state.rank4 = newData.name;
          state.gainIds[3] = newData.id;
          break;
        case 5:
          state.rank5 = newData.name;
          state.gainIds[4] = newData.id;
          break;
      }
    },
    togglePerk: (state: MartialPerksStore, newData: any) => {
      state.martialPerks[
        newData.perkGroup as keyof MartialPerksStore["martialPerks"]
      ][newData.i].known = newData.known;
      state.martialPerks[
        newData.perkGroup as keyof MartialPerksStore["martialPerks"]
      ][newData.i].id = newData.id;
    },
    clearMartialPerks: (state: MartialPerksStore) => {
      const arr = Object.keys(state.martialPerks);
      for (const perkgroup of arr) {
        for (let i = 0; i < perkgroup.length; i++) {
          state.martialPerks[
            perkgroup as keyof MartialPerksStore["martialPerks"]
          ][i].known = false;
        }
      }
    },
  },
  actions: {
    updateMartialPerk: (
      context: any,
      newData: {
        known: boolean;
        i: number;
        perkGroup: string;
        character_id: string;
      }
    ) => {
      const perkData =
        context.getters.martialPerks[
          newData.perkGroup as keyof MartialPerksStore["martialPerks"]
        ][newData.i];
      const name = perkData.name;
      const rank = perkData.rank;
      const id = perkData.id;
      if (newData.known) {
        putMartialPerks(
          newData.character_id,
          newData.perkGroup,
          name,
          rank,
          id
        ).then((response) => {
          context.commit("togglePerk", {
            perkGroup: newData.perkGroup,
            i: newData.i,
            known: newData.known,
            id: response.data[0].id,
          });
        });
      } else {
        deleteMartialPerks(
          newData.character_id,
          newData.perkGroup,
          name,
          rank,
          id
        );
        context.commit("togglePerk", {
          perkGroup: newData.perkGroup,
          i: newData.i,
          known: newData.known,
          id: id,
        });
      }
    },
    updatePerkGain: (
      context: any,
      newData: {
        gain: string[];
        character_id: string;
      }
    ) => {
      let rank = 1;
      const ids = context.getters.gainIds;
      context.commit("setMartialGain", {
        ranks: newData.gain,
        ids: ids,
      });
      let idNum = 0;
      console.log(ids);
      for (let i = 0; i < newData.gain.length; i++) {
        const g = newData.gain[i];
        const id = ids[i];
        putMartialPerkGain(newData.character_id, g, rank, id).then(
          (response) => {
            context.commit("setMartialGainId", {
              position: idNum,
              id: response.data[0].id,
            });
            idNum++;
          }
        );
        rank++;
      }
    },
  },
};
